import type { AxiosResponse } from 'axios';
import { ModelResource } from './resource';
import { Attachment, ModelWithAttachments, type PresignedUrl } from '../models';

export class ResourceWithAttachments<M extends ModelWithAttachments, C = any> extends ModelResource<
  M,
  C
> {
  async getAttachmentPresignedUrl(
    entityId: string,
    fileName: string,
    mimeType: string,
  ): Promise<{ attachment: Attachment; presignedUrl: PresignedUrl }> {
    return this.api
      .post(this.path(entityId, 'attachments', 'presign'), {
        data: { filename: fileName, mime_type: mimeType },
      })
      .then(
        (
          res: AxiosResponse<{
            attachment: Attachment;
            presigned_url: PresignedUrl;
          }>,
        ) => {
          return {
            attachment: Attachment.fromJson(res.data.attachment),
            presignedUrl: res.data.presigned_url,
          };
        },
      );
  }

  async removeAttachment(entity: M, attachment: Attachment): Promise<boolean> {
    const index = entity.attachments?.indexOf(attachment) ?? -1;

    attachment.failed_to_delete = false;
    entity.removeAttachment(attachment);

    return this.api
      .delete(this.path(entity.id, 'attachments', attachment.id))
      .then(() => true)
      .catch((err) => {
        if (index !== -1) entity.attachments.splice(index, 0, attachment);
        attachment.failed_to_delete = true;
        throw err;
      });
  }

  async verifyAttachment(entity: M, attachment: Attachment): Promise<Attachment> {
    attachment.is_uploading = true;

    return this.api
      .post(this.path(entity.id, 'attachments', attachment.id, 'verify'))
      .then((att) => {
        attachment.updateFromJson(att.data);

        return attachment;
      })
      .finally(() => {
        attachment.is_uploading = false;
      });
  }
}
